import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Package, Plus, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { redemptionStatusLabel } from '../api';
import { useAdminRewards, useAllRedemptions, useGamesMutations } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** חנות הפרסים והבקשות (PRD 5.8.4). */
export function RewardsAdminPage() {
  const confirm = useConfirm();
  const canManage = usePerm('games.manage');
  const undo = useUndo();
  const rewards = useAdminRewards();
  const redemptions = useAllRedemptions();
  const mutations = useGamesMutations();
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const waiting = (redemptions.data ?? []).filter((item) => item.status === 'requested');
  const handled = (redemptions.data ?? []).filter((item) => item.status !== 'requested');

  return (
    <AdminPage
      title="פרסים"
      subtitle="מה אפשר לקבל בנקודות, ומי ביקש"
      actions={
        !isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            פרס חדש
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <Panel title="פרס חדש">
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const name = String(form.get('name') ?? '').trim();
              const cost = Number(form.get('cost') ?? 0);
              if (!name || cost <= 0) return;
              const stock = String(form.get('stock') ?? '').trim();
              run(async () => {
                await mutations.createReward.mutateAsync({
                  name,
                  description: String(form.get('description') ?? '').trim() || null,
                  cost_points: cost,
                  stock: stock ? Number(stock) : null,
                  is_active: true,
                });
                setIsAdding(false);
              });
            }}
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-1 sm:col-span-2">
                <label htmlFor="name" className="text-body-sm text-muted">
                  שם הפרס
                </label>
                <input id="name" name="name" className={FIELD} required />
              </div>
              <div className="grid gap-1">
                <label htmlFor="cost" className="text-body-sm text-muted">
                  מחיר בנקודות
                </label>
                <input id="cost" name="cost" type="number" min={1} className={FIELD} required />
              </div>
            </div>

            <div className="grid gap-1">
              <label htmlFor="description" className="text-body-sm text-muted">
                תיאור
              </label>
              <input id="description" name="description" className={FIELD} />
            </div>

            <div className="grid gap-1">
              <label htmlFor="stock" className="text-body-sm text-muted">
                מלאי
              </label>
              <input
                id="stock"
                name="stock"
                type="number"
                min={0}
                placeholder="ריק = בלי הגבלה"
                className={`${FIELD} sm:w-48`}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit">
                שמירת הפרס
              </Button>
              <Button onClick={() => setIsAdding(false)}>ביטול</Button>
            </div>
          </form>
        </Panel>
      )}

      {waiting.length > 0 && (
        <Panel title={`ממתינות לאישור (${waiting.length})`}>
          <Table>
            <thead>
              <Tr>
                <Th>עובד/ת</Th>
                <Th>פרס</Th>
                <Th>עלות</Th>
                <Th>תאריך</Th>
                <Th>
                  <span className="sr-only">פעולות</span>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {waiting.map((item) => (
                <Tr key={item.id}>
                  <Td>
                    {item.employees
                      ? `${item.employees.first_name} ${item.employees.last_name}`
                      : '—'}
                  </Td>
                  <Td>{item.rewards?.name}</Td>
                  <Td className="tabular">{item.rewards?.cost_points}</Td>
                  <Td className="tabular">{formatDate(item.created_at)}</Td>
                  <Td>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="primary"
                        onClick={() =>
                          run(() =>
                            mutations.decideRedemption.mutateAsync({ id: item.id, approve: true }),
                          )
                        }
                      >
                        <Check size={18} strokeWidth={1.75} aria-hidden="true" />
                        אישור
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          confirm.ask({
                            title: 'דחיית בקשה',
                            tone: 'warn',
                            body: (
                              <p className="m-0">
                                הנקודות יוחזרו לעובד/ת, והוא/היא יקבלו על כך התראה.
                              </p>
                            ),
                            confirmLabel: 'דחייה והחזרת הנקודות',
                            onConfirm: () =>
                              run(() =>
                                mutations.decideRedemption.mutateAsync({
                                  id: item.id,
                                  approve: false,
                                }),
                              ),
                          });
                        }}
                      >
                        <X size={18} strokeWidth={1.75} aria-hidden="true" />
                        דחייה
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}

      {rewards.isPending && <Skeleton className="h-40" />}
      {rewards.isError && <ErrorState onRetry={() => void rewards.refetch()} />}

      {rewards.data?.length === 0 && !isAdding && (
        <EmptyState
          title="אין עדיין פרסים"
          description="פרס הוא משהו שאפשר לממש בנקודות שנצברו."
        />
      )}

      {rewards.data && rewards.data.length > 0 && (
        <Panel title="הפרסים">
          <Table>
            <thead>
              <Tr>
                <Th>שם</Th>
                <Th>מחיר</Th>
                <Th>מלאי</Th>
                <Th>מצב</Th>
                <Th>
                  <span className="sr-only">פעולות</span>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {rewards.data.filter((reward) => !undo.isPending(reward.id)).map((reward) => (
                <Tr key={reward.id}>
                  <Td>{reward.name}</Td>
                  <Td className="tabular">{reward.cost_points}</Td>
                  <Td className="tabular">
                    {reward.stock === null ? 'בלי הגבלה' : reward.stock}
                  </Td>
                  <Td>
                    {reward.is_active ? (
                      <StatusBadge tone="good">פעיל</StatusBadge>
                    ) : (
                      <span className="text-muted">כבוי</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          run(() =>
                            mutations.updateReward.mutateAsync({
                              id: reward.id,
                              patch: { is_active: !reward.is_active },
                            }),
                          )
                        }
                      >
                        {reward.is_active ? 'כיבוי' : 'הפעלה'}
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`מחיקת ${reward.name}`}
                        onClick={() => {
                          undo.remove({
                            id: reward.id,
                            message: `"${reward.name}" נמחק`,
                            perform: () => mutations.removeReward.mutateAsync(reward.id),
                          });
                        }}
                      >
                        <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}

      {handled.length > 0 && (
        <Panel title="בקשות שטופלו">
          <Table>
            <thead>
              <Tr>
                <Th>עובד/ת</Th>
                <Th>פרס</Th>
                <Th>מצב</Th>
                <Th>
                  <span className="sr-only">פעולות</span>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {handled.map((item) => (
                <Tr key={item.id}>
                  <Td>
                    {item.employees
                      ? `${item.employees.first_name} ${item.employees.last_name}`
                      : '—'}
                  </Td>
                  <Td>{item.rewards?.name}</Td>
                  <Td>
                    <StatusBadge tone={item.status === 'rejected' ? 'bad' : 'good'}>
                      {redemptionStatusLabel[item.status]}
                    </StatusBadge>
                  </Td>
                  <Td>
                    {item.status === 'approved' && (
                      <Button
                        variant="ghost"
                        onClick={() => run(() => mutations.markDelivered.mutateAsync(item.id))}
                      >
                        <Package size={18} strokeWidth={1.75} aria-hidden="true" />
                        נמסר
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}
      {confirm.dialog}
    </AdminPage>
  );
}
